import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  DndContext,
  closestCenter,
  useDroppable,
  useDraggable,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { SingleChoiceAnswerBlock } from '@course-authoring/shared-types';
import { useTheme, useAccountConfig, useResultsHidden } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

import {
  createRichTextRendererExtensions,
  createRichTextContentResetStyles,
  safeGenerateRichTextHTML,
  safeGenerateNonEmptyRichTextHTML,
} from './richTextRenderer';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { getStandardInteractionWidth } from './interactionLayout';
import { useDefaultDndSensors } from './hooks/useDefaultDndSensors';
import { useCommittedQuestionNarrationOutcome } from './hooks/useCommittedQuestionNarrationOutcome';
import { ThankYouMessage } from './ThankYouMessage';
import { createContentBlockStyles, interactionCard } from './styles/contentBlocks';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import { ActionButton } from './ActionButton';
import type { QuestionNarrationOutcomeHandler } from './narrationEvents';
import { ResponseThresholdEmptyState } from './ResponseThresholdEmptyState';
import { localizeResultsDistribution } from './resultsLocalization';
import { RichTextContent } from './RichTextContent';

export type SingleChoiceAnswerResultsData = {
  /** Total responses shown in the "My Org" legend entry. */
  orgCount: number;
  /** Total responses shown in the "Global" legend entry. */
  communityCount: number;
  /** Per-option percentages in block.options order; falls back to seeded placeholder data. */
  orgPercentages?: readonly number[];
  /** Per-option percentages in block.options order; falls back to seeded placeholder data. */
  communityPercentages?: readonly number[];
  /**
   * False hides the "Global" legend entry and bar (NA-72: the account
   * hasn't reached its own response threshold yet). When both this and
   * `orgAvailable` are false, the renderer shows the "not enough responses"
   * empty state instead of an empty legend/bar list. Defaults to true.
   */
  communityAvailable?: boolean;
  /**
   * False hides the "My Org" legend entry and bar entirely (NA-69: the
   * account has excluded its own org data). Defaults to true.
   */
  orgAvailable?: boolean;
};

// Placeholder counts shown until real aggregate results are available; hosts
// can inject real data via the resultsData prop.
const PLACEHOLDER_RESULTS_DATA: SingleChoiceAnswerResultsData = {
  orgCount: 716,
  communityCount: 3584,
};

export interface SingleChoiceAnswerRendererProps {
  block: SingleChoiceAnswerBlock;
  onInteractionComplete?: () => void;
  /**
   * For an ungated question, fires once with the learner's single submitted
   * option (NA-178 response logging). For a gated question, fires on EVERY
   * submit attempt — a wrong pick, then any retries, then the eventual
   * correct pick — so the caller can record when the block is completed.
   */
  onAnswerSubmit?: (answer: { optionId: string }) => void;
  onNarrationOutcomeCommitted?: QuestionNarrationOutcomeHandler;
  /**
   * Aggregate results to display; defaults to placeholder data. `'pending'`
   * (NA-72) renders a lightweight loading message while the real results
   * fetch is in flight, instead of the fake placeholder data.
   */
  resultsData?: SingleChoiceAnswerResultsData | 'pending';
  /**
   * Whether the player has found a prior recorded answer for this block on
   * bootstrap (NA-319). `'loading'` renders a lightweight placeholder in
   * place of both the question and results/complete views, so a learner
   * can't submit a new answer while the player is still checking. Defaults
   * to `'unanswered'` (today's behavior) for any caller that doesn't opt in,
   * e.g. authoring previews.
   *
   * For a gated block, a prior answer only seeds the locked completed view
   * when it equals `block.correctOptionId`. The player passes the correct
   * option here once the server reports the block as completed, even though
   * the stored answer is the learner's first (possibly wrong) pick. A stored
   * answer that doesn't match the correct option and isn't reported as
   * completed means the learner abandoned the attempt, so the block stays
   * interactive rather than showing the "Correct!" view.
   */
  priorAnswerStatus?: 'loading' | 'answered' | 'unanswered';
  /** The learner's previously recorded `answerOptionId`, present only when `priorAnswerStatus === 'answered'`. */
  priorAnswerOptionId?: string;
}

type GatedPhase = 'interactive' | 'complete';
type UngatedPhase = 'interactive' | 'results';

const DROP_ZONE_ID = '__answer_zone__';

const CONTENT_CLASS = 'single-choice-rich-content';

const RESULTS_BAR_HEIGHT_PX = 28;

const OPTION_CARD_MIN_HEIGHT_PX = 84;

// AA-178 mobile spec: compact option rows and a fixed-height answer zone once
// the two-column layout stacks. The zone needs an explicit min-height because
// stacked flex children no longer stretch to match the options column.
const MOBILE_OPTION_CARD_MIN_HEIGHT_PX = 50;

const MOBILE_ANSWER_ZONE_MIN_HEIGHT_PX = 100;

// Stack point for the options/answer-zone columns. Starts at the standard
// mobile tier; tuned independently of the 768/576 typography tiers (same
// pattern as Likert's block-specific layout breakpoints).
const SINGLE_CHOICE_MOBILE_LAYOUT_BREAKPOINT = 768;

// --- Seeded random for fake results ---

function seededRandom(seed: string): () => number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  return () => {
    hash = (hash * 1103515245 + 12345) | 0;
    return ((hash >> 16) & 0x7fff) / 0x7fff;
  };
}

function generateFakePercentages(count: number, seed: string): number[] {
  const rng = seededRandom(seed);
  const raw = Array.from({ length: count }, () => rng() + 0.1);
  const total = raw.reduce((a, b) => a + b, 0);
  const percentages = raw.map((v) => Math.round((v / total) * 100));
  // Largest-remainder correction so the values sum to 100 without dumping
  // the whole rounding error on one bucket.
  let diff = 100 - percentages.reduce((a, b) => a + b, 0);
  const step = diff > 0 ? 1 : -1;
  const order = raw
    .map((v, i) => ({ i, remainder: (v / total) * 100 - Math.round((v / total) * 100) }))
    .sort((a, b) => (step > 0 ? b.remainder - a.remainder : a.remainder - b.remainder));
  for (let k = 0; diff !== 0 && order.length > 0; k = (k + 1) % order.length) {
    const target = order[k];
    if (target === undefined) break;
    percentages[target.i] = (percentages[target.i] ?? 0) + step;
    diff -= step;
  }
  return percentages;
}

// --- Drag handle icon ---

function DragHandleIcon(): React.ReactElement {
  return (
    <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor" aria-hidden="true">
      <circle cx="3" cy="3" r="1.5" />
      <circle cx="9" cy="3" r="1.5" />
      <circle cx="3" cy="8" r="1.5" />
      <circle cx="9" cy="8" r="1.5" />
      <circle cx="3" cy="13" r="1.5" />
      <circle cx="9" cy="13" r="1.5" />
    </svg>
  );
}

// --- Draggable option card ---

function DraggableOptionCard({
  id,
  label,
  isDisabled,
  isEliminated,
  isGreyedOut,
  isSelected,
  isCorrectHighlight,
  showDragHandle,
  onClick,
  style,
}: {
  id: string;
  label: string;
  isDisabled: boolean;
  isEliminated: boolean;
  isGreyedOut: boolean;
  isSelected: boolean;
  isCorrectHighlight: boolean;
  showDragHandle: boolean;
  onClick: () => void;
  style: CSSProperties;
}) {
  const { tokens } = useTheme();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled: isDisabled,
  });

  const transformStyle = transform
    ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
    : undefined;

  let bgColor = style.backgroundColor as string;
  let borderColor = style.borderColor as string;

  if (isCorrectHighlight) {
    bgColor = tokens.choice.correct.bg;
    borderColor = tokens.choice.correct.border;
  } else if (isEliminated) {
    bgColor = tokens.backgrounds.app;
    borderColor = tokens.borders.interactive;
  } else if (isGreyedOut) {
    bgColor = tokens.backgrounds.surface;
    borderColor = tokens.borders.default;
  }

  const textColor = isGreyedOut ? tokens.text.subtle : 'inherit';

  // Ghost element: stays at original position, shown grey while the card is being dragged
  const ghost = isDragging ? (
    <div
      aria-hidden
      style={{
        ...style,
        backgroundColor: tokens.backgrounds.surface,
        borderColor: tokens.borders.default,
        position: 'absolute' as const,
        inset: 0,
        zIndex: 0,
      }}
    >
      <span style={{ flex: 1, color: tokens.text.subtle }}>{label}</span>
    </div>
  ) : null;

  return (
    <div style={{ position: 'relative' as const }}>
      {ghost}
      <div
        ref={setNodeRef}
        {...(isDisabled ? {} : listeners)}
        {...(isDisabled ? {} : attributes)}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-label={label}
        aria-disabled={isDisabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDisabled) onClick();
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
            e.preventDefault();
            onClick();
          }
        }}
        style={{
          ...style,
          backgroundColor: bgColor,
          borderColor,
          boxShadow: isEliminated ? 'none' : (style.boxShadow as string | undefined),
          transform: transformStyle,
          cursor: isDisabled ? 'default' : isDragging ? 'grabbing' : 'grab',
          outline: isSelected ? `2px solid ${tokens.choice.drag.border}` : 'none',
          userSelect: 'none' as const,
          position: 'relative' as const,
          zIndex: isDragging ? 10 : 1,
          transition: isGreyedOut ? 'background-color 200ms, border-color 200ms' : undefined,
        }}
      >
        <span
          style={{
            flex: 1,
            textDecoration: isEliminated ? 'line-through' : 'none',
            textDecorationThickness: isEliminated ? '1px' : undefined,
            color: isDragging ? 'inherit' : textColor,
            transition: isGreyedOut ? 'color 200ms' : undefined,
          }}
        >
          {label}
        </span>
        {showDragHandle && !isDisabled && !isEliminated && (
          <span
            style={{
              position: 'absolute' as const,
              right: tokens.spacing.lg,
              top: '50%',
              transform: 'translateY(-50%)',
              color: tokens.text.subtle,
            }}
          >
            <DragHandleIcon />
          </span>
        )}
      </div>
    </div>
  );
}

// --- Droppable answer zone ---

function AnswerDropZone({
  children,
  style,
}: {
  children: React.ReactNode;
  style: CSSProperties;
}) {
  const { tokens } = useTheme();
  const { setNodeRef, isOver } = useDroppable({ id: DROP_ZONE_ID });
  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        ...(isOver ? { borderColor: tokens.choice.drag.border, backgroundColor: tokens.choice.hover.bg } : {}),
      }}
    >
      {children}
    </div>
  );
}

// --- Results bar chart (ungated) ---

function ResultsBarChart({
  block,
  selectedOptionId,
  resultsData: resultsDataProp,
  style,
}: {
  block: SingleChoiceAnswerBlock;
  selectedOptionId: string;
  resultsData: SingleChoiceAnswerResultsData | 'pending';
  style: {
    legend: CSSProperties;
    legendItem: CSSProperties;
    legendSwatch: CSSProperties;
    resultOption: CSSProperties;
    resultOptionSelected: CSSProperties;
    yourAnswerLabel: CSSProperties;
    optionLabel: CSSProperties;
    optionLabelBold: CSSProperties;
    bar: CSSProperties;
    barLabel: CSSProperties;
  };
}) {
  const { t, i18n } = useTranslation();
  const { tokens } = useTheme();
  const isResultsPending = resultsDataProp === 'pending';
  // While pending, treat both sections as unavailable - same hide-everything-
  // but-the-answer treatment as below-threshold, just with a different
  // message below, rather than rendering fake percentages while real data
  // is still in flight.
  const resultsData: SingleChoiceAnswerResultsData = isResultsPending
    ? { orgCount: 0, communityCount: 0, orgAvailable: false, communityAvailable: false }
    : resultsDataProp;

  const orgPercentages = useMemo(
    () => resultsData.orgPercentages ?? generateFakePercentages(block.options.length, block.id + '-org'),
    [resultsData.orgPercentages, block.options.length, block.id]
  );
  const communityPercentages = useMemo(
    () => resultsData.communityPercentages ?? generateFakePercentages(block.options.length, block.id + '-community'),
    [resultsData.communityPercentages, block.options.length, block.id]
  );

  // Animate bars from zero on mount
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const { orgCount, communityCount, communityAvailable = true, orgAvailable = true } = resultsData;
  const isBelowResponseThreshold = !isResultsPending && !orgAvailable && !communityAvailable;
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const resultsLocalizationKeys = {
    myOrgTitle: 'results.my_org',
    globalTitle: 'results.global',
    responsesOne: 'results.responses_one',
    responsesOther: 'results.responses_other',
  };
  const orgLabel = localizeResultsDistribution(
    t,
    locale,
    { id: 'my-org', responseCount: orgCount },
    resultsLocalizationKeys,
  );
  const communityLabel = localizeResultsDistribution(
    t,
    locale,
    { id: 'global', responseCount: communityCount },
    resultsLocalizationKeys,
  );

  return (
    <>
      <div style={style.legend}>
        {orgAvailable && (
          <div style={style.legendItem}>
            <div style={{ ...style.legendSwatch, backgroundColor: tokens.bar.myOrg }} />
            <span>{orgLabel.title} ({orgLabel.responsesLabel})</span>
          </div>
        )}
        {communityAvailable && (
          <div style={style.legendItem}>
            <div style={{ ...style.legendSwatch, backgroundColor: tokens.bar.global }} />
            <span>{communityLabel.title} ({communityLabel.responsesLabel})</span>
          </div>
        )}
      </div>

      {block.options.map((option, index) => {
        const isSelected = option.id === selectedOptionId;
        // Below threshold (or still pending), only the learner's own selected
        // option is shown (per NA-72's own-answer-stays-visible requirement) -
        // the other, unselected options carry no real aggregate data worth
        // listing.
        if ((isBelowResponseThreshold || isResultsPending) && !isSelected) return null;

        const orgPct = orgPercentages[index] ?? 0;
        const communityPct = communityPercentages[index] ?? 0;

        return (
          <div
            key={option.id}
            style={isSelected ? style.resultOptionSelected : style.resultOption}
          >
            {isSelected && <div style={style.yourAnswerLabel}>{t('results.your_answer')}</div>}
            <div style={isSelected ? style.optionLabelBold : style.optionLabel}>
              {option.label}
            </div>
            {orgAvailable && (
              <div
                style={{
                  ...style.bar,
                  backgroundColor: tokens.bar.myOrg,
                  width: animated ? `${Math.max(orgPct, 3)}%` : '0%',
                }}
              >
                {animated && (
                  <span style={{ ...style.barLabel, color: tokens.text.onInverse }}>
                    {orgPct}%
                  </span>
                )}
              </div>
            )}
            {communityAvailable && (
              <div
                style={{
                  ...style.bar,
                  backgroundColor: tokens.bar.global,
                  width: animated ? `${Math.max(communityPct, 3)}%` : '0%',
                }}
              >
                {animated && (
                  <span style={{ ...style.barLabel, color: tokens.text.primary }}>
                    {communityPct}%
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      {isBelowResponseThreshold && <ResponseThresholdEmptyState />}
      {isResultsPending && <ResponseThresholdEmptyState variant="loading" />}
    </>
  );
}

// --- Main renderer ---

export function SingleChoiceAnswerRenderer({
  block,
  onInteractionComplete,
  onAnswerSubmit,
  onNarrationOutcomeCommitted,
  resultsData: resultsDataInput,
  priorAnswerStatus = 'unanswered',
  priorAnswerOptionId,
}: SingleChoiceAnswerRendererProps): React.ReactElement {
  const { t } = useTranslation();
  const resultsHidden = useResultsHidden(block.hideResults);
  const { hideOrg } = useAccountConfig();
  // Placeholder data (Preview) must respect the account's My Org toggle (NA-344).
  const resultsData =
    resultsDataInput ??
    (hideOrg ? { ...PLACEHOLDER_RESULTS_DATA, orgAvailable: false } : PLACEHOLDER_RESULTS_DATA);
  const hasEmittedAnswerRef = useRef(false);
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const isMobileLayout = useIsMobileViewport(SINGLE_CHOICE_MOBILE_LAYOUT_BREAKPOINT);
  const interactionWidth = getStandardInteractionWidth(tokens, isMobile);

  // Rich text rendering
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const resetStyles = useMemo(
    () => createRichTextContentResetStyles(CONTENT_CLASS),
    []
  );

  const descriptionHtml = useMemo(() => {
    if (!block.description) return '';
    /**
     * NA-382: must distinguish "no question text" from "question text
     * deliberately overridden to blank" — the latter is a truthy doc holding an
     * empty paragraph, and plain generation turns it into '<p></p>', which
     * renders as a visible empty block. Same guard NA-313 added for the
     * explanation; Likert and Workplace Color Spectrum already had it.
     */
    return safeGenerateNonEmptyRichTextHTML(block.description, extensions);
  }, [block.description, extensions]);

  const correctExplanationHtml = useMemo(() => {
    // NA-313: must distinguish "no explanation" from "an explanation deliberately
    // overridden to blank" — the latter is a truthy doc holding an empty
    // paragraph, and plain generation turns it into '<p></p>', which renders as a
    // visible empty panel. The non-empty variant returns null for both, and for
    // whitespace-only content, so the panel is simply not rendered.
    //
    // It also falls back to extracted plain text when generation yields nothing
    // usable, which the plain generator does not. WorkplaceColorSpectrumRenderer
    // already used this helper; this brings the two in line.
    return safeGenerateNonEmptyRichTextHTML(block.correctExplanation, extensions);
  }, [block.correctExplanation, extensions]);

  // Options display in authored order
  const displayOptions = block.options;

  // State
  // NA-319: a prior answer only seeds the locked view when it's verifiably
  // safe to do so — ungated always locks from any stored answer, but gated
  // only locks when the stored answer equals the correct option (see the
  // prop doc comment above for why a wrong stored answer is left
  // unrehydrated instead of risking a false "Correct!" completion).
  const initialPriorSeed =
    priorAnswerStatus === 'answered' && priorAnswerOptionId
      ? (block.gated ? (priorAnswerOptionId === block.correctOptionId ? priorAnswerOptionId : null) : priorAnswerOptionId)
      : null;
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(() => initialPriorSeed);
  const [eliminatedIds, setEliminatedIds] = useState<Set<string>>(new Set());
  const [gatedPhase, setGatedPhase] = useState<GatedPhase>(() =>
    block.gated && initialPriorSeed ? 'complete' : 'interactive'
  );
  const [ungatedPhase, setUngatedPhase] = useState<UngatedPhase>(() =>
    !block.gated && initialPriorSeed ? 'results' : 'interactive'
  );
  const [submittedOptionId, setSubmittedOptionId] = useState<string | null>(() => initialPriorSeed);
  const [wrongFeedbackOptionId, setWrongFeedbackOptionId] = useState<string | null>(null);

  // NA-319: the bootstrap prior-answers fetch normally resolves after this
  // block's first mount, not before it - so the lazy useState seeding above
  // only covers the (uncommon) case where it's already known at mount. This
  // effect covers the common case: the prop transitions from 'loading' to
  // 'answered' after mount. Deliberately does not call onAnswerSubmit or
  // onInteractionComplete - those are live-submission side effects, not
  // appropriate for seeding a state that was already recorded in a prior
  // session (see NA-319 design.md decision 4).
  useEffect(() => {
    if (priorAnswerStatus !== 'answered' || !priorAnswerOptionId) return;
    if (block.gated) {
      if (priorAnswerOptionId !== block.correctOptionId || gatedPhase === 'complete') return;
      setSelectedOptionId(priorAnswerOptionId);
      setSubmittedOptionId(priorAnswerOptionId);
      setGatedPhase('complete');
    } else {
      if (ungatedPhase === 'results') return;
      setSelectedOptionId(priorAnswerOptionId);
      setSubmittedOptionId(priorAnswerOptionId);
      setUngatedPhase('results');
    }
  }, [priorAnswerStatus, priorAnswerOptionId, block.gated, block.correctOptionId, gatedPhase, ungatedPhase]);
  let narrationOutcome: Parameters<QuestionNarrationOutcomeHandler>[0] | null = null;
  let narrationPresentation: Parameters<QuestionNarrationOutcomeHandler>[1] | null = null;
  let narrationOutcomeKey: string | null = null;
  if (!block.gated && ungatedPhase === 'results' && submittedOptionId) {
    narrationOutcome = 'submitted';
    narrationPresentation = resultsHidden ? 'hidden-results' : 'visible-results';
    narrationOutcomeKey = `${narrationOutcome}:${submittedOptionId}:${narrationPresentation}`;
  } else if (block.gated && gatedPhase === 'complete' && submittedOptionId) {
    narrationOutcome = 'correct';
    narrationPresentation = 'feedback';
    narrationOutcomeKey = `${narrationOutcome}:${submittedOptionId}`;
  } else if (block.gated && wrongFeedbackOptionId) {
    narrationOutcome = 'incorrect';
    narrationPresentation = 'feedback';
    narrationOutcomeKey = `${narrationOutcome}:${wrongFeedbackOptionId}`;
  }
  useCommittedQuestionNarrationOutcome({
    outcomeKey: narrationOutcomeKey,
    outcome: narrationOutcome,
    presentation: narrationPresentation,
    onCommitted: onNarrationOutcomeCommitted,
  });

  const sharedStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);

  // DnD sensors
  const sensors = useDefaultDndSensors();

  const isComplete = block.gated ? gatedPhase === 'complete' : ungatedPhase === 'results';

  // Handlers
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || over.id !== DROP_ZONE_ID) return;

      const optionId = active.id as string;
      if (eliminatedIds.has(optionId)) return;

      setWrongFeedbackOptionId(null);
      setSelectedOptionId(optionId);
    },
    [eliminatedIds]
  );

  const handleOptionClick = useCallback(
    (optionId: string) => {
      if (eliminatedIds.has(optionId) || isComplete) return;
      setWrongFeedbackOptionId(null);
      setSelectedOptionId((prev) => (prev === optionId ? null : optionId));
    },
    [eliminatedIds, isComplete]
  );

  const handleSubmit = useCallback(() => {
    if (!selectedOptionId) return;

    // Gated: every submit attempt re-emits so the server can record when the
    // correct option is submitted (see useQuestionResponseLogger). Ungated:
    // first-submit-only, since its UI never allows a second real submit.
    if (block.gated || !hasEmittedAnswerRef.current) {
      hasEmittedAnswerRef.current = true;
      onAnswerSubmit?.({ optionId: selectedOptionId });
    }

    if (block.gated) {
      if (selectedOptionId === block.correctOptionId) {
        setGatedPhase('complete');
        setSubmittedOptionId(selectedOptionId);
        setWrongFeedbackOptionId(null);
        onInteractionComplete?.();
      } else {
        setEliminatedIds((prev) => new Set([...prev, selectedOptionId]));
        setWrongFeedbackOptionId(selectedOptionId);
        setSelectedOptionId(null);
      }
    } else {
      setUngatedPhase('results');
      setSubmittedOptionId(selectedOptionId);
      onInteractionComplete?.();
    }
  }, [selectedOptionId, block.gated, block.correctOptionId, onInteractionComplete, onAnswerSubmit]);

  // Styles
  // NA-227: same three-tier content-block heading resolution as WCS/Likert so
  // the H2 title sizes identically across viewports (40 → 30 <768 → 26 ≤576).
  const titleTypography = useMemo(
    () => getContentBlockHeadingTypography(tokens, block.titleLevel ?? 'h2', { isMobile, isSmallMobile }),
    [block.titleLevel, isMobile, isSmallMobile, tokens],
  );

  const selectedLabel = useMemo(() => {
    if (!selectedOptionId) return '';
    const opt = block.options.find((o) => o.id === selectedOptionId);
    return opt?.label ?? '';
  }, [selectedOptionId, block.options]);

  const wrongFeedbackLabel = useMemo(() => {
    if (!wrongFeedbackOptionId) return '';
    const opt = block.options.find((o) => o.id === wrongFeedbackOptionId);
    return opt?.label ?? '';
  }, [wrongFeedbackOptionId, block.options]);

  const s = useMemo(() => ({
    wrapper: {
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      width: interactionWidth,
      maxWidth: '100%',
      margin: '0 auto',
    },
    title: {
      fontSize: titleTypography.size,
      fontWeight: titleTypography.weight,
      lineHeight: titleTypography.lineHeight,
      margin: 0,
      marginBottom: isMobileLayout ? tokens.spacing.md : tokens.spacing.xs,
    },
    description: {
      // NA-205/NA-227: match WCS/Likert — reading copy steps to 16px at ≤576.
      fontSize: isSmallMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
      lineHeight: tokens.typography.body.lineHeight,
      color: tokens.text.primary,
      marginBottom: tokens.spacing.xl,
    },
    card: {
      ...interactionCard(tokens),
      padding: tokens.spacing.xl,
    },
    instruction: {
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.regular,
      fontStyle: 'italic',
      color: tokens.text.primary,
      marginBottom: tokens.spacing.lg,
    },
    columnsContainer: {
      display: 'flex',
      // AA-178: stack options above the answer zone on mobile.
      flexDirection: isMobileLayout ? ('column' as const) : ('row' as const),
      gap: isMobileLayout ? tokens.spacing.lg : tokens.spacing.lgPlus,
      alignItems: 'stretch',
    },
    optionsColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: isMobileLayout ? tokens.spacing.sm : tokens.spacing.lgPlus,
    },
    dropZoneColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
    },
    optionCard: {
      position: 'relative' as const,
      boxSizing: 'border-box' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: isMobileLayout
        ? `${MOBILE_OPTION_CARD_MIN_HEIGHT_PX}px`
        : `${OPTION_CARD_MIN_HEIGHT_PX}px`,
      // Mobile rows are compact and tap-only (no drag handle), so padding is
      // symmetric; desktop reserves right-side room for the handle.
      padding: isMobileLayout
        ? `${tokens.spacing.md} ${tokens.spacing.lg}`
        : `${tokens.spacing.lgPlus} ${tokens.spacing["3xl"]} ${tokens.spacing.lgPlus} 30px`,
      backgroundColor: tokens.backgrounds.app,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.borders.interactive,
      borderRadius: tokens.radius.xs,
      boxShadow: tokens.shadow.lg,
      // NA-232: labels step 20 → 18 <768 → 16 ≤576, staying level with prose.
      fontSize: isSmallMobile
        ? tokens.typography.mobile.body.size
        : isMobileLayout
          ? tokens.typography.sizes.ui
          : tokens.typography.body.size,
      fontWeight: tokens.typography.weights.regular,
      fontFamily: tokens.typography.fontFamily,
      textAlign: isMobileLayout ? ('center' as const) : undefined,
      transition: 'background-color 150ms, border-color 150ms',
    },
    dropZone: {
      boxSizing: 'border-box' as const,
      borderWidth: '1px',
      borderStyle: 'dashed',
      borderColor: tokens.borders.dropZone,
      borderRadius: tokens.radius.xs,
      flex: 1,
      minHeight: isMobileLayout ? `${MOBILE_ANSWER_ZONE_MIN_HEIGHT_PX}px` : undefined,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'border-color 150ms, background-color 150ms',
    },
    dropZonePlaceholder: {
      fontSize: isSmallMobile
        ? tokens.typography.mobile.body.size
        : isMobileLayout
          ? tokens.typography.sizes.ui
          : tokens.typography.body.size,
      color: tokens.text.primary,
      fontWeight: tokens.typography.weights.regular,
    },
    dropZoneFilled: {
      boxSizing: 'border-box' as const,
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: tokens.choice.hover.border,
      borderRadius: tokens.radius.xs,
      flex: 1,
      minHeight: isMobileLayout ? `${MOBILE_ANSWER_ZONE_MIN_HEIGHT_PX}px` : undefined,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: tokens.choice.hover.bg,
      padding: `${tokens.spacing.lg} ${tokens.spacing["2xl"]}`,
    },
    dropZoneIncorrect: {
      boxSizing: 'border-box' as const,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.choice.wrong.border,
      borderRadius: tokens.radius.xs,
      flex: 1,
      minHeight: isMobileLayout ? `${MOBILE_ANSWER_ZONE_MIN_HEIGHT_PX}px` : undefined,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: tokens.choice.wrong.bg,
      padding: `${tokens.spacing.lg} ${tokens.spacing["2xl"]}`,
    },
    dropZoneIncorrectLabel: {
      fontSize: tokens.typography.body.size,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: tokens.typography.lineHeights.tight,
      color: tokens.text.primary,
      marginBottom: tokens.spacing.md,
      textAlign: 'center' as const,
    },
    dropZoneIncorrectMessage: {
      // AA-178: zone emphasis text steps 30 → 20 on mobile.
      fontSize: isMobileLayout ? tokens.typography.sizes.md : tokens.typography.sizes.h3,
      fontWeight: 650,
      lineHeight: tokens.typography.lineHeights.tight,
      color: tokens.text.primary,
      textAlign: 'center' as const,
    },
    dropZoneCorrect: {
      boxSizing: 'border-box' as const,
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: tokens.choice.correct.border,
      borderRadius: tokens.radius.xs,
      flex: 1,
      minHeight: isMobileLayout ? `${MOBILE_ANSWER_ZONE_MIN_HEIGHT_PX}px` : undefined,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: tokens.choice.correct.bg,
      padding: `${tokens.spacing.lg} ${tokens.spacing["2xl"]}`,
    },
    dropZoneLabel: {
      fontSize: tokens.typography.body.size,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      marginBottom: tokens.spacing.sm,
      textAlign: 'center' as const,
    },
    dropZoneCorrectLabel: {
      fontSize: tokens.typography.body.size,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      marginBottom: tokens.spacing.sm,
      textAlign: 'center' as const,
    },
    dropZoneAnswer: {
      // AA-178: zone emphasis text steps 30 → 20 on mobile.
      fontSize: isMobileLayout ? tokens.typography.sizes.md : tokens.typography.sizes.h3,
      fontWeight: 650,
      lineHeight: tokens.typography.lineHeights.tight,
      color: tokens.text.primary,
      textAlign: 'center' as const,
    },
    submitButton: {
      ...sharedStyles.actionButton,
      display: 'block',
      margin: `${isMobileLayout ? tokens.spacing.lg : tokens.spacing.xl} auto 0`,
      transition: `background-color ${tokens.ui.transitionDefault}, color ${tokens.ui.transitionDefault}`,
    },
    explanationArea: isMobileLayout
      ? {
        // AA-178: explanation sits centered under the card at full width.
        marginTop: tokens.spacing.lgPlus,
        width: '100%',
        fontSize: isSmallMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
        lineHeight: tokens.typography.body.lineHeight,
        color: tokens.text.primary,
        textAlign: 'center' as const,
      }
      : {
        marginTop: tokens.spacing['2xl'],
        marginLeft: `calc(${tokens.spacing.xl} + 3px + 30px)`,
        marginRight: 'auto',
        width: '70%',
        fontSize: tokens.typography.body.size,
        lineHeight: tokens.typography.body.lineHeight,
        color: tokens.text.primary,
        textAlign: 'left' as const,
      },
    // Results panel + bar chart styles (Figma node 3352:997).
    // NA-232: mobile tightens padding/gaps to the interactive card's rhythm —
    // no AA-178 frame exists for this state; values ruled by PO.
    resultsPanel: {
      ...interactionCard(tokens),
      display: 'flex',
      flexDirection: 'column' as const,
      gap: isMobileLayout ? tokens.spacing.lg : tokens.spacing.xl,
      backgroundColor: tokens.backgrounds.resultsPanel,
      padding: isMobileLayout
        ? tokens.spacing.xl
        : `${tokens.spacing.xl} ${tokens.spacing['3xl']} ${tokens.spacing['3xl']}`,
    },
    legend: {
      display: 'flex',
      // Stacked on mobile: the two legend labels wrap mid-label at phone
      // widths. Right-aligned to keep the desktop anchor.
      flexDirection: isMobileLayout ? ('column' as const) : ('row' as const),
      justifyContent: 'flex-end',
      alignItems: isMobileLayout ? ('flex-end' as const) : undefined,
      gap: isMobileLayout ? tokens.spacing.sm : tokens.spacing.md,
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      // NA-232: steps with the bar-% labels at ≤576 — same annotation layer.
      fontSize: isSmallMobile ? tokens.typography.sizes.sm : tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: tokens.typography.lineHeights.normal,
      color: tokens.text.primary,
    },
    legendSwatch: {
      width: '16px',
      height: '16px',
      borderRadius: tokens.radius.xs,
      flexShrink: 0,
    },
    resultOption: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.lg} ${tokens.spacing.xl} ${tokens.spacing.lgPlus}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.app,
      justifyContent: 'center',
    },
    resultOptionSelected: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.lg} ${tokens.spacing.xl} ${tokens.spacing.lgPlus}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.choice.hover.bg,
      // NA-242: selected-result highlight uses the fixed UI accent — never the
      // account accent.
      border: `2px solid ${tokens.ui.fixedAccent}`,
    },
    yourAnswerLabel: {
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: tokens.typography.lineHeights.normal,
      color: tokens.bar.myOrg,
      marginBottom: `-${tokens.spacing.xs}`,
    },
    optionLabel: {
      // NA-232: matches the mobile option-card label steps (20 → 18 → 16).
      fontSize: isSmallMobile
        ? tokens.typography.mobile.body.size
        : isMobileLayout
          ? tokens.typography.sizes.ui
          : tokens.typography.body.size,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: tokens.typography.lineHeights.normal,
      color: tokens.text.primary,
    },
    optionLabelBold: {
      fontSize: isSmallMobile
        ? tokens.typography.mobile.body.size
        : isMobileLayout
          ? tokens.typography.sizes.ui
          : tokens.typography.body.size,
      fontWeight: tokens.typography.weights.bold,
      lineHeight: tokens.typography.lineHeights.normal,
      color: tokens.text.primary,
    },
    bar: {
      height: `${RESULTS_BAR_HEIGHT_PX}px`,
      boxSizing: 'border-box' as const,
      borderRadius: tokens.radius.xs,
      display: 'flex',
      alignItems: 'center',
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      minWidth: '40px',
      transition: 'width 500ms ease-out',
    },
    barLabel: {
      // NA-232: % labels step 18 → 14 at ≤576 so they sit inside narrow bars.
      fontSize: isSmallMobile ? tokens.typography.sizes.sm : tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: tokens.typography.lineHeights.normal,
      whiteSpace: 'nowrap' as const,
    },
  }), [interactionWidth, isMobileLayout, isSmallMobile, titleTypography, tokens, sharedStyles]);

  const submitDisabled = !selectedOptionId || isComplete;

  // --- Render loading placeholder while the bootstrap prior-answer lookup is unresolved (NA-319) ---
  if (priorAnswerStatus === 'loading') {
    return (
      <div style={s.wrapper}>
        <style>{resetStyles}</style>
        {block.title && <div style={s.title}>{block.title}</div>}
        {descriptionHtml && (
          <RichTextContent
            className={CONTENT_CLASS}
            style={s.description}
            html={descriptionHtml}
          />
        )}
        <ResponseThresholdEmptyState variant="checking-answer" />
      </div>
    );
  }

  // --- Render ungated results ---
  if (!block.gated && ungatedPhase === 'results' && submittedOptionId) {
    if (resultsHidden) {
      return (
        <div style={s.wrapper}>
          <style>{resetStyles}</style>
          {block.title && <div style={s.title}>{block.title}</div>}
          {descriptionHtml && (
            <RichTextContent
              className={CONTENT_CLASS}
              style={s.description}
              html={descriptionHtml}
            />
          )}
          <ThankYouMessage visible />
        </div>
      );
    }
    return (
      <div style={s.wrapper}>
        <style>{resetStyles}</style>
        {block.title && <div style={s.title}>{block.title}</div>}
        {descriptionHtml && (
          <RichTextContent
            className={CONTENT_CLASS}
            style={s.description}
            html={descriptionHtml}
          />
        )}
        <div style={s.resultsPanel}>
          <ResultsBarChart block={block} selectedOptionId={submittedOptionId} resultsData={resultsData} style={s} />
        </div>
      </div>
    );
  }

  // --- Render gated complete ---
  if (block.gated && gatedPhase === 'complete' && submittedOptionId) {
    const correctOption = block.options.find((o) => o.id === block.correctOptionId);
    return (
      <div style={s.wrapper}>
        <style>{resetStyles}</style>
        {block.title && <div style={s.title}>{block.title}</div>}
        {descriptionHtml && (
          <RichTextContent
            className={CONTENT_CLASS}
            style={s.description}
            html={descriptionHtml}
          />
        )}
        <div style={s.card}>
          <div style={s.columnsContainer}>
            {/* Left: all options with final styling */}
            <div style={s.optionsColumn}>
              {displayOptions.map((option) => {
                const isCorrect = option.id === block.correctOptionId;
                return (
                  <div
                    key={option.id}
                    style={{
                      ...s.optionCard,
                      backgroundColor: isCorrect ? tokens.backgrounds.app : 'transparent',
                      boxShadow: 'none',
                      cursor: 'default',
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        textDecoration: isCorrect ? 'none' : 'line-through',
                        textDecorationThickness: isCorrect ? undefined : '1px',
                      }}
                    >
                      {option.label}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Right: correct answer in green zone */}
            <div style={s.dropZoneColumn}>
              <div style={s.dropZoneCorrect}>
                <div style={s.dropZoneCorrectLabel}>{t('question_feedback.correct')}</div>
                <div style={s.dropZoneAnswer}>{correctOption?.label}</div>
              </div>
            </div>
          </div>
        </div>
        {/* Explanation (below the card) */}
        {correctExplanationHtml && (
          <RichTextContent
            className={CONTENT_CLASS}
            style={s.explanationArea}
            html={correctExplanationHtml}
          />
        )}
      </div>
    );
  }

  // --- Render interactive state ---
  return (
    <div style={s.wrapper}>
      <style>{resetStyles}</style>
      {block.title && <div style={s.title}>{block.title}</div>}
      {descriptionHtml && (
        <RichTextContent
          className={CONTENT_CLASS}
          style={s.description}
          html={descriptionHtml}
        />
      )}
      {/* AA-178: on mobile the instruction sits above the card, not inside it */}
      {isMobileLayout && <div style={s.instruction}>{t('single_choice.select_below')}</div>}
      <div style={s.card}>
        {!isMobileLayout && <div style={s.instruction}>{t('single_choice.select_below')}</div>}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div style={s.columnsContainer}>
            {/* Left: option cards */}
            <div style={s.optionsColumn}>
              {displayOptions.map((option) => {
                const isEliminated = eliminatedIds.has(option.id);
                const isInDropZone = selectedOptionId === option.id;

                return (
                  <DraggableOptionCard
                    key={option.id}
                    id={option.id}
                    label={option.label}
                    isDisabled={isEliminated || isInDropZone}
                    isEliminated={isEliminated}
                    isGreyedOut={isInDropZone}
                    isSelected={false}
                    isCorrectHighlight={false}
                    showDragHandle={!isMobileLayout}
                    onClick={() => handleOptionClick(option.id)}
                    style={s.optionCard}
                  />
                );
              })}
            </div>

            {/* Right: drop zone — always render a single instance */}
            <div style={s.dropZoneColumn}>
              <AnswerDropZone
                style={
                  selectedOptionId
                    ? s.dropZoneFilled
                    : wrongFeedbackOptionId
                      ? s.dropZoneIncorrect
                      : s.dropZone
                }
              >
                {selectedOptionId ? (
                  <>
                    <div style={s.dropZoneLabel}>{t('results.your_answer')}</div>
                    <div style={s.dropZoneAnswer}>{selectedLabel}</div>
                  </>
                ) : wrongFeedbackOptionId ? (
                  <>
                    <div style={s.dropZoneIncorrectLabel}>{wrongFeedbackLabel}</div>
                    <div style={s.dropZoneIncorrectMessage}>
                      {t('question_feedback.incorrect')}
                      {isMobileLayout ? ' ' : <br />}
                      {t('question_feedback.try_again')}
                    </div>
                  </>
                ) : (
                  <span style={s.dropZonePlaceholder}>
                    {isMobileLayout
                      ? t('single_choice.select_above')
                      : t('single_choice.drag_here')}
                  </span>
                )}
              </AnswerDropZone>
            </div>
          </div>

        </DndContext>
      </div>

      {/* Submit button */}
      <ActionButton
        disabled={submitDisabled}
        onClick={handleSubmit}
        baseStyle={s.submitButton}
        disabledStyle={sharedStyles.actionButtonDisabled}
        hoverStyle={sharedStyles.actionButtonHover}
      >
        {t('submit_reveal.submit')}
      </ActionButton>
    </div>
  );
}

SingleChoiceAnswerRenderer.displayName = 'SingleChoiceAnswerRenderer';
